import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react'
import { readTheme, saveTheme } from '../theme/frost'
import { gsap } from 'gsap'
import styles from './LanguagePartnerView.module.scss'
import voices from '../speech/voices'
import { pickVoice } from '../speech/speak'
import recognitionLangs from '../speech/recognitionLangs'
import languages from '../languages.js'
import flags from '../flags.js'
import { celebrate } from './juice'
import {
  tokenizeLine, romanizationOf, cancelTts, mergeTranscript, phraseCloseEnough, looksWrongScript,
} from '../speech/flowMatch'

import MicIcon from '@mui/icons-material/Mic'
import MicOffIcon from '@mui/icons-material/MicOff'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import ReplayIcon from '@mui/icons-material/Replay'
import KeyboardIcon from '@mui/icons-material/Keyboard'
import SendIcon from '@mui/icons-material/Send'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import { macStudioFetch, macStudioModels } from '../ollama'

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition

const LANGUAGES = Object.entries(languages).map(([voice, label]) => ({
  voice,
  label,
  flag: flags[voice] ?? '🌐'
}))
const langByCode = (code, fallback) =>
  LANGUAGES.find(l => l.voice === code) || fallback

const SCENARIOS = [
  { id: 'casual', label: 'Casual Chat', icon: '🗣' },
  { id: 'cafe', label: 'Coffee Shop', icon: '☕️' },
  { id: 'airport', label: 'Airport / Customs', icon: '🛂' },
  { id: 'directions', label: 'Asking Directions', icon: '🗺' },
  { id: 'hotel', label: 'Hotel Check-In', icon: '🏨' },
  { id: 'shopping', label: 'Shopping', icon: '🛒' },
  { id: 'custom', label: 'Custom Scenario...', icon: '✍️' }
]

// gemma4:26b holds a conversation well — it keeps track of who said what and
// stays in the target language — and, as a mixture of experts, answers fast;
// it's the Mac Studio's model for everything now. gemma3:27b stays as the
// alternative while it's installed.
// Light / dark, shared with the lesson, Special Mode, home and reader views
// through the same key, so one choice carries across all of them.
// Day or night: the site's one choice (theme/frost.js) — the computer's until chosen.

const DEFAULT_MODEL = 'gemma4:26b'
const MODELS = ['gemma4:26b', 'gemma3:27b']

const MODES = [
  { id: 'tutor', label: 'tutor', hint: 'Coaches you in your own language, feeds you phrases to try.' },
  { id: 'guided', label: 'guided', hint: 'Pick a suggested reply and read it aloud, word by word.' },
  { id: 'free', label: 'free', hint: 'Say anything — no target line to match.' },
]

const normPhrase = (s) => String(s || '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim()
const samePhrase = (a, b) => normPhrase(a) && normPhrase(a) === normPhrase(b)

const store = {
  get: (k, d) => { try { return window.localStorage.getItem(k) || d } catch { return d } },
  set: (k, v) => { try { window.localStorage.setItem(k, v) } catch { /* private mode */ } },
}

// Web Speech has no "queue with a voice per chunk" primitive, so we drive the
// chain ourselves — this is what lets one tutor turn be spoken partly in
// English and partly in the target language, each with the right voice.
function speakSequence(segments, voiceSpeed, tokenRef, token) {
  if (!('speechSynthesis' in window)) return
  try { window.speechSynthesis.cancel() } catch { /* fine */ }
  const list = (segments || []).filter(s => s.text && s.text.trim())
  let i = 0
  const next = () => {
    if (tokenRef.current !== token) return   // superseded or view hidden
    if (i >= list.length) return
    const seg = list[i++]
    const u = new SpeechSynthesisUtterance(seg.text)
    const { voice, lang } = pickVoice(seg.voiceURI)
    if (voice) u.voice = voice
    if (lang) u.lang = lang
    u.rate = voiceSpeed
    u.onend = next
    u.onerror = next
    window.speechSynthesis.speak(u)
  }
  if (window.speechSynthesis.getVoices().length === 0) {
    window.speechSynthesis.addEventListener('voiceschanged', next, { once: true })
  } else next()
}

export default function LanguagePartnerView({ voiceSpeed, isVisible, setIsVisible }) {
  const [messages, setMessages] = useState([])   // {role, content, segments?, try?, tip?, translation?}
  const [target, setTarget] = useState(null)     // the phrase you're practising aloud
  const [wordStates, setWordStates] = useState({ forKey: '', states: [] })

  const [mode, setMode] = useState(() => store.get('lp.mode', 'tutor'))
  const [suggestions, setSuggestions] = useState([])
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)
  const [loadingChat, setLoadingChat] = useState(false)

  const [listening, setListening] = useState(false)
  const [interim, setInterim] = useState('')
  const [micPhase, setMicPhase] = useState('off')
  const [micError, setMicError] = useState(null)
  // Which language the recognizer is set to when you're NOT reading a target
  // line. Web Speech takes one language at a time — see the note in the UI.
  const [answerLang, setAnswerLang] = useState('native')

  const [showRoman, setShowRoman] = useState(true)
  const [showTranslation, setShowTranslation] = useState(true)
  const [autoSpeak, setAutoSpeak] = useState(true)

  // Your language vs the one you're learning — kept separate so the tutor can
  // explain in one and drill you in the other.
  const [nativeLang, setNativeLang] = useState(() => langByCode(store.get('lp.native', 'eng'), LANGUAGES[0]))
  const [targetLang, setTargetLang] = useState(() => langByCode(store.get('lp.target', 'rus'), LANGUAGES[1]))
  const [selectedScenario, setSelectedScenario] = useState(SCENARIOS[0])
  const [customScenarioText, setCustomScenarioText] = useState('')
  // A stored choice is kept, except the one-time move off the old default —
  // otherwise nobody who has used this before would ever see the better model.
  const [selectedModel, setSelectedModel] = useState(() => {
    const stored = store.get('lp.model', '')
    if (!stored || !MODELS.includes(stored)) return DEFAULT_MODEL
    if (!store.get('lp.modelGemma4', false)) return DEFAULT_MODEL
    return stored
  })
  useEffect(() => { store.set('lp.modelGemma4', true) }, [])
  const [theme, setTheme] = useState(readTheme)
  useEffect(() => {
    const follow = (e) => setTheme(e.detail || readTheme())
    window.addEventListener('frost-theme', follow)
    return () => window.removeEventListener('frost-theme', follow)
  }, [])
  const [settingsOpen, setSettingsOpen] = useState(false)

  const [showKeyboard, setShowKeyboard] = useState(false)
  const [input, setInput] = useState('')

  const viewportRef = useRef(null)
  const trackRef = useRef(null)
  const lineRefs = useRef([])
  const inputRef = useRef(null)
  const recRef = useRef(null)
  const listeningRef = useRef(false)
  const completedRef = useRef(false)
  const sendTimerRef = useRef(null)
  const sessionRef = useRef(0)
  const deadSessionsRef = useRef(0)
  const freeTranscriptRef = useRef('')
  const chatControllerRef = useRef(null)
  const suggestControllerRef = useRef(null)
  const messagesRef = useRef([])
  const autoSpeakRef = useRef(autoSpeak)
  const debounceRef = useRef(null)
  const ttsTokenRef = useRef(0)
  const startedRef = useRef(false)
  // Phrases the tutor has already set as a "try" — fed back into the prompt so
  // it stops re-teaching the same greeting every turn.
  const practisedRef = useRef([])
  // The recognizer is created once per session and its handlers close over
  // whatever `target` was at that moment. Tapping a suggestion starts listening
  // in the same tick as setTarget, so the closure would still see null and skip
  // word matching entirely — read through refs instead.
  const targetRef = useRef(null)
  const targetWordsRef = useRef([])
  // Everything finalised for the current turn. Chrome makes us recycle the
  // recognizer after each final result (see onresult), which resets
  // event.results — so we keep the running text ourselves.
  const heardRef = useRef('')
  // True once the recognizer has finalised an utterance — i.e. you've actually
  // stopped speaking. Partial credit waits for this so it can't fire while
  // you're still mid-phrase.
  const sawFinalRef = useRef(false)
  // Lets a live session respawn a fresh recognizer without a stale closure.
  const startListeningRef = useRef(null)


  useEffect(() => { messagesRef.current = messages }, [messages])
  useEffect(() => { autoSpeakRef.current = autoSpeak }, [autoSpeak])
  useEffect(() => { store.set('lp.native', nativeLang.voice) }, [nativeLang])
  useEffect(() => { store.set('lp.target', targetLang.voice) }, [targetLang])
  useEffect(() => { store.set('lp.mode', mode) }, [mode])
  useEffect(() => { store.set('lp.model', selectedModel) }, [selectedModel])

  // Ask Ollama what's actually installed rather than trusting a hard-coded
  // list — falls back to MODELS if the endpoint isn't reachable.
  const [availableModels, setAvailableModels] = useState(MODELS)
  useEffect(() => {
    if (!isVisible) return
    let cancelled = false
    ;(async () => {
      try {
        const names = (await macStudioModels()).map(m => m.name).filter(Boolean).sort()
        if (!cancelled && names.length) setAvailableModels(names)
      } catch { /* keep the fallback list */ }
    })()
    return () => { cancelled = true }
  }, [isVisible])

  // Never drop the current choice from the list, even if it isn't installed.
  const modelOptions = useMemo(
    () => (availableModels.includes(selectedModel) ? availableModels : [selectedModel, ...availableModels]),
    [availableModels, selectedModel]
  )

  const lines = useMemo(() => {
    const base = messages.map((m, i) => ({ ...m, key: `m${i}` }))
    if (target) base.push({ role: 'user', content: target, pending: true, key: 'pending' })
    return base
  }, [messages, target])

  const idx = lines.length - 1
  const line = lines[idx]
  const targetWords = useMemo(
    () => (target ? tokenizeLine(target, targetLang.voice) : []),
    [target, targetLang.voice]
  )
  const states = wordStates.forKey === (target || '') ? wordStates.states : []

  useEffect(() => {
    targetRef.current = target
    targetWordsRef.current = targetWords
    heardRef.current = ''
    sawFinalRef.current = false
    setWordStates({ forKey: target || '', states: Array(targetWords.length).fill('pending') })
    setInterim('')
    completedRef.current = false
  }, [target, targetWords])

  useLayoutEffect(() => {
    const el = lineRefs.current[idx]
    const viewport = viewportRef.current
    if (!el || !viewport || !trackRef.current) return
    const y = viewport.clientHeight / 2 - el.offsetTop - el.offsetHeight / 2
    gsap.to(trackRef.current, { y, duration: 0.8, ease: 'power3.out' })
  }, [idx, lines])

  const scenarioText = useCallback((sc = selectedScenario, custom = customScenarioText) => {
    if (sc.id === 'casual') return 'an open, friendly everyday conversation'
    if (sc.id === 'custom') return custom || 'everyday life'
    return sc.label
  }, [selectedScenario, customScenarioText])

  const stopListening = useCallback(() => {
    listeningRef.current = false
    setListening(false)
    setMicPhase('off')
    sessionRef.current++
    try { recRef.current?.stop() } catch { /* already stopped */ }
  }, [])

  const stopSpeaking = useCallback(() => {
    ttsTokenRef.current++
    cancelTts()
  }, [])

  // Speak a bilingual turn (tutor) or a single-language line.
  const speakSegments = useCallback((segs) => {
    const token = ++ttsTokenRef.current
    speakSequence(segs, voiceSpeed, ttsTokenRef, token)
  }, [voiceSpeed])

  const speakOne = useCallback((text, langCode) => {
    if (!text) return
    speakSegments([{ text, voiceURI: voices[langCode] || voices['eng'] }])
  }, [speakSegments])

  const segsToVoiced = useCallback((segments) => (segments || []).map(s => ({
    text: s.text,
    voiceURI: voices[s.lang === 'target' ? targetLang.voice : nativeLang.voice] || voices['eng'],
  })), [targetLang, nativeLang])

  // ── Model calls ────────────────────────────────────────────────────────────

  const callGenerate = useCallback(async (prompt, opts, signal) => {
    const res = await macStudioFetch('generate', JSON.stringify({ model: selectedModel, prompt, stream: false, keep_alive: '30m', format: 'json', options: opts }), { signal })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    return JSON.parse(data.response.replace(/```json|```/g, '').trim())
  }, [selectedModel])

  const historyText = (msgs) => (msgs.length
    ? msgs.map(m => `${m.role === 'assistant' ? 'Tutor' : 'Learner'}: ${m.content}`).join('\n')
    : 'No previous messages.')

  // Tutor turn: mostly the learner's own language, with target-language phrases
  // tagged so each chunk gets the right voice.
  const tutorSystem = useCallback((nat, tgt, sc, custom, taught) => `You are a warm, encouraging ${tgt.label} tutor. The learner is a native ${nat.label} speaker, a nervous beginner.
Topic: ${scenarioText(sc, custom)}.

Every turn you MUST:
- Speak mostly in ${nat.label}, weaving in ONE short ${tgt.label} phrase.
- MOVE THE LESSON ON. Teach something NEW each turn — never re-offer a phrase you already taught.
- React to what the learner actually just said. If their message was garbled, misheard or wrong, acknowledge it kindly in one short clause and then move on to something new — do NOT ask for the same phrase again.
- Make sure the ${tgt.label} is correct, natural and genuinely means what you say it means in ${nat.label}.
- Keep the whole turn under 40 words. You are the tutor: never write the learner's line for them.
${taught.length ? `\nAlready taught — you may NOT use any of these again: ${taught.map(p => `"${p}"`).join(', ')}` : ''}

Reply with JSON only:
{"say":"<1-2 short ${nat.label} sentences reacting to what they just said and setting up the next phrase>",
 "try":"<ONE new short ${tgt.label} phrase, max 8 words, for them to say now>",
 "tip":"<one short ${nat.label} hint: pronunciation, grammar, or how their last answer could improve>"}

"say" must be written in ${nat.label}. "try" must be written in ${tgt.label} and in its native script — never Chinese characters unless ${tgt.label} is Chinese. Do not put the ${tgt.label} phrase inside "say"; it belongs only in "try".`, [scenarioText])

  // A real system+roles chat call. Flattening the transcript into one prompt is
  // what made a small model lose track of who said what — it started echoing the
  // learner ("I'm ready!") and re-teaching the same greeting forever.
  const requestTutorTurn = useCallback(async (msgs, nat, tgt, sc, custom, signal, nudge) => {
    const chatMsgs = [
      { role: 'system', content: tutorSystem(nat, tgt, sc, custom, practisedRef.current) },
      ...msgs.map(({ role, content }) => ({ role, content })),
    ]
    if (nudge) chatMsgs.push({ role: 'system', content: nudge })
    const res = await macStudioFetch('chat', JSON.stringify({
        model: selectedModel, messages: chatMsgs, stream: false, keep_alive: '30m',
        format: 'json', options: { temperature: 0.75, num_predict: 320 }
      }), { signal })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    try {
      return JSON.parse((data.message?.content || '').replace(/```json|```/g, '').trim())
    } catch { return null }
  }, [tutorSystem, selectedModel])

  const runChatTurn = useCallback(async (msgs, nat, tgt, sc, custom) => {
    if (chatControllerRef.current) chatControllerRef.current.abort()
    chatControllerRef.current = new AbortController()
    const signal = chatControllerRef.current.signal

    if (mode === 'tutor') {
      // Reject a turn that recycles a taught phrase, answers in the wrong
      // script, or comes back malformed.
      const bad = (p) => !p || !String(p.say || '').trim() || !String(p.try || '').trim()
        || practisedRef.current.some(q => samePhrase(q, p.try))
        || looksWrongScript(p.try, tgt.voice)

      let parsed = await requestTutorTurn(msgs, nat, tgt, sc, custom, signal)
      if (bad(parsed)) {
        const retry = await requestTutorTurn(msgs, nat, tgt, sc, custom, signal,
          `That answer was rejected — you repeated a phrase, used the wrong script, or returned malformed JSON. Teach a DIFFERENT ${tgt.label} phrase in ${tgt.label}'s own script, not one of: ${practisedRef.current.join(', ') || '(none yet)'}. Return valid JSON only.`)
        if (retry && !bad(retry)) parsed = retry
      }

      const say = String(parsed?.say || '').trim()
      const tryPhrase = String(parsed?.try || '').trim() || null
      // Build the segments ourselves so the big green line, the "try saying"
      // chip and the phrase being matched are guaranteed to be the same string.
      const segments = [
        ...(say ? [{ lang: 'native', text: say }] : []),
        ...(tryPhrase ? [{ lang: 'target', text: tryPhrase }] : []),
      ]
      if (tryPhrase) practisedRef.current = [...practisedRef.current, tryPhrase].slice(-12)
      return {
        role: 'assistant',
        content: segments.map(s => s.text).join(' ').trim() || 'Let’s keep going.',
        segments,
        try: tryPhrase,
        tip: (parsed?.tip || '').trim() || null,
      }
    }

    // Target-only modes: a normal immersive reply.
    const res = await macStudioFetch('chat', JSON.stringify({
        model: selectedModel,
        messages: [
          {
            role: 'system',
            // "Do not use English unless they ask" is too weak for a capable
            // model: it reads the learner as a beginner and helpfully adds a
            // gloss in brackets after every sentence. The app already fetches
            // the translation separately and shows it underneath, so the reply
            // must be target-language only, said positively and concretely.
            content: `You are a friendly language partner in this scenario: ${scenarioText(sc, custom)}. Chat with the learner in ${tgt.label}. Keep replies natural and short (1-2 sentences).

Write ONLY ${tgt.label}. Your entire reply must be the ${tgt.label} text a native speaker would say, and nothing else:
- No ${nat.label} anywhere, not even in brackets.
- No translation, gloss, explanation, pronunciation guide or romanisation. The app shows the learner a translation already, so adding one repeats it.
- Do not label the speaker or add notes about your reply.
So: write the greeting on its own, never the greeting followed by its ${nat.label} meaning in brackets.`
          },
          ...msgs.map(({ role, content }) => ({ role, content }))
        ],
        stream: false, keep_alive: '30m',
        options: { temperature: 0.7 }
      }), { signal })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    return { role: 'assistant', content: data.message?.content || '' }
  }, [mode, requestTutorTurn, selectedModel, scenarioText])

  const fetchSuggestions = useCallback(async (msgs, tgt, nat, sc, custom) => {
    if (suggestControllerRef.current) suggestControllerRef.current.abort()
    suggestControllerRef.current = new AbortController()
    setLoadingSuggestions(true)
    try {
      const parsed = await callGenerate(
        `You are helping a native ${nat.label} speaker reply in ${tgt.label}.
Topic: ${scenarioText(sc, custom)}.
Conversation so far:
${historyText(msgs)}

Suggest 3 different natural replies they could say next, written in ${tgt.label}. Each short and speakable (max 8 words).

Respond ONLY with valid JSON: {"replies":["one","two","three"]}`,
        { temperature: 0.8, num_predict: 140 },
        suggestControllerRef.current.signal
      )
      setSuggestions((parsed.replies || []).filter(r => r && !looksWrongScript(r, tgt.voice)))
    } catch (err) {
      if (err.name !== 'AbortError') setSuggestions([])
    }
    setLoadingSuggestions(false)
  }, [callGenerate, scenarioText])

  const fetchCompletions = useCallback(async (sentence, msgs) => {
    if (suggestControllerRef.current) suggestControllerRef.current.abort()
    suggestControllerRef.current = new AbortController()
    setLoadingSuggestions(true)
    try {
      const parsed = await callGenerate(
        `Autocomplete a learner's reply written in ${targetLang.label}.
Conversation so far:
${historyText(msgs)}
They are typing: "${sentence}"
Complete it 3 different short ways (max 8 words each), in ${targetLang.label}.

Respond ONLY with valid JSON: {"replies":["one","two","three"]}`,
        { temperature: 0.8, num_predict: 90 },
        suggestControllerRef.current.signal
      )
      setSuggestions((parsed.replies || []).filter(r => r && !looksWrongScript(r, targetLang.voice)))
    } catch (err) {
      if (err.name !== 'AbortError') setSuggestions([])
    }
    setLoadingSuggestions(false)
  }, [callGenerate, targetLang])

  const startConversation = useCallback(async (nat, tgt, sc, custom) => {
    stopListening()
    stopSpeaking()
    practisedRef.current = []
    setMessages([])
    setTarget(null)
    setSuggestions([])
    setInput('')
    setMicError(null)
    setLoadingChat(true)
    try {
      const msg = await runChatTurn([], nat, tgt, sc, custom)
      setMessages([msg])
      if (autoSpeakRef.current) {
        speakSegments(msg.segments ? segsToVoiced(msg.segments)
          : [{ text: msg.content, voiceURI: voices[tgt.voice] }])
      }
      if (mode !== 'tutor') fetchSuggestions([msg], tgt, nat, sc, custom)
    } catch (err) {
      if (err.name !== 'AbortError') setMicError(`Couldn't start: ${err.message}`)
    }
    setLoadingChat(false)
  }, [runChatTurn, mode, fetchSuggestions, stopListening, stopSpeaking, speakSegments, segsToVoiced])

  const sendUserText = useCallback(async (text) => {
    const clean = (text || '').trim()
    if (!clean) return
    stopListening()
    setTarget(null)
    setSuggestions([])
    setInput('')
    freeTranscriptRef.current = ''

    const next = [...messagesRef.current, { role: 'user', content: clean }]
    setMessages(next)
    setLoadingChat(true)
    try {
      const msg = await runChatTurn(next, nativeLang, targetLang, selectedScenario, customScenarioText)
      setMessages([...next, msg])
      if (autoSpeakRef.current) {
        speakSegments(msg.segments ? segsToVoiced(msg.segments)
          : [{ text: msg.content, voiceURI: voices[targetLang.voice] }])
      }
      if (mode !== 'tutor') {
        fetchSuggestions([...next, msg], targetLang, nativeLang, selectedScenario, customScenarioText)
      }
    } catch (err) {
      if (err.name !== 'AbortError') setMicError(`Reply failed: ${err.message}`)
    }
    setLoadingChat(false)
  }, [runChatTurn, nativeLang, targetLang, selectedScenario, customScenarioText, mode, fetchSuggestions, stopListening, speakSegments, segsToVoiced])

  // ── Speech recognition ─────────────────────────────────────────────────────

  // One language at a time is all Web Speech allows. While reading a target
  // line we force the target language; otherwise you choose.
  const recCode = target ? targetLang.voice : (answerLang === 'target' ? targetLang.voice : nativeLang.voice)
  const recLangLabel = recognitionLangs[recCode] || 'en-US'

  const applyTranscript = useCallback((transcript, isFinal) => {
    const tgt = targetRef.current
    if (isFinal) sawFinalRef.current = true
    if (!tgt) { freeTranscriptRef.current = transcript; return }
    const words = targetWordsRef.current
    setWordStates(prev => {
      if (prev.forKey !== tgt) return prev
      // If the whole utterance matches closely enough, accept it outright —
      // per-word matching shouldn't block someone who said the line correctly.
      if (phraseCloseEnough(transcript, tgt, targetLang.voice)) {
        return { forKey: prev.forKey, states: prev.states.map(() => 'hit') }
      }
      return {
        forKey: prev.forKey,
        states: mergeTranscript(prev.states, transcript, words, targetLang.voice, isFinal),
      }
    })
  }, [targetLang.voice])

  const startListening = useCallback(() => {
    if (!SpeechRecognition) {
      setMicError('Speech recognition is not supported in this browser.')
      return
    }
    setMicError(null)
    stopSpeaking()
    const session = ++sessionRef.current
    // The line this session belongs to. `continuous` recognition accumulates
    // event.results for the whole session, so a session opened before the
    // current line would replay earlier speech against it and complete it
    // instantly. Results from a session that predates the line are ignored.
    const sessionTarget = targetRef.current
    const rec = new SpeechRecognition()
    // Read the language off the ref, not a captured render value — practise()
    // starts listening in the same tick it sets the target, so the closure
    // would otherwise still say "native" and listen in the wrong language.
    const liveCode = targetRef.current
      ? targetLang.voice
      : (answerLang === 'target' ? targetLang.voice : nativeLang.voice)
    rec.lang = recognitionLangs[liveCode] || 'en-US'
    rec.continuous = true
    rec.interimResults = true
    let heardSound = false

    const stopWithError = (msg) => {
      listeningRef.current = false
      setListening(false)
      setMicPhase('off')
      setMicError(msg)
    }

    rec.onaudiostart = () => { if (session === sessionRef.current) setMicPhase('open') }
    rec.onsoundstart = () => {
      if (session !== sessionRef.current) return
      heardSound = true; deadSessionsRef.current = 0; setMicPhase('sound')
    }
    rec.onspeechstart = () => {
      if (session !== sessionRef.current) return
      heardSound = true; deadSessionsRef.current = 0; setMicPhase('speech')
    }
    rec.onresult = (event) => {
      if (session !== sessionRef.current) return
      if (targetRef.current !== sessionTarget) return   // line changed under us
      heardSound = true; deadSessionsRef.current = 0; setMicPhase('speech')
      let chunk = ''
      let sawFinal = false
      for (let i = 0; i < event.results.length; i++) {
        chunk += event.results[i][0].transcript + ' '
        if (event.results[i].isFinal) sawFinal = true
      }
      const combined = `${heardRef.current} ${chunk.trim()}`.trim()
      setInterim(combined.slice(-70))
      applyTranscript(combined, sawFinal)

      if (sawFinal) {
        // In continuous mode Chrome routinely goes deaf after emitting a final
        // result — no further results, and no onend, so the keep-alive below
        // never notices and you have to toggle the mic by hand. Bank what we
        // heard and swap in a fresh recognizer instead.
        heardRef.current = combined
        sessionRef.current++
        try { rec.abort() } catch { /* already gone */ }
        setTimeout(() => {
          if (!listeningRef.current || completedRef.current) return
          startListeningRef.current?.()
        }, 200)
      }
    }
    rec.onerror = (e) => {
      if (session !== sessionRef.current) return
      console.warn('[partner] recognition error:', e.error, 'lang:', rec.lang)
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        stopWithError('Microphone access was blocked — allow it in browser settings.')
      } else if (e.error === 'audio-capture') {
        stopWithError('No audio device captured — pick the right microphone in the browser/site settings.')
      } else if (e.error === 'language-not-supported') {
        stopWithError(`This browser can't recognize ${rec.lang} speech.`)
      } else if (e.error === 'network') {
        setMicError('Speech service network error — retrying…')
      }
    }
    rec.onend = () => {
      if (session !== sessionRef.current) return
      if (!listeningRef.current) { setMicPhase('off'); return }
      if (completedRef.current) return
      if (!heardSound) {
        deadSessionsRef.current++
        if (deadSessionsRef.current >= 4) {
          stopWithError('Mic is open but no sound is reaching it. Click the 🔒/camera icon in the address bar, pick your real microphone, then tap the mic again.')
          return
        }
      }
      setTimeout(() => {
        if (session !== sessionRef.current || !listeningRef.current) return
        try { rec.start() } catch { /* raced with a manual start */ }
      }, 500)
    }

    recRef.current = rec
    listeningRef.current = true
    deadSessionsRef.current = 0
    setListening(true)
    try {
      rec.start()
    } catch {
      setTimeout(() => {
        if (session !== sessionRef.current || !listeningRef.current) return
        try { rec.start() } catch { /* user can toggle the mic */ }
      }, 400)
    }
  }, [recCode, applyTranscript, stopSpeaking, targetLang.voice, nativeLang.voice, answerLang])

  useEffect(() => { startListeningRef.current = startListening }, [startListening])

  // Reading a target line: celebrate, then send it as your turn.
  // Every word hit → go straight away. Short of that, only accept once you've
  // stopped speaking AND a grace window passes with nothing new landing — this
  // effect re-runs on every state change, so finishing the last word cancels
  // the partial accept and completes the line properly instead.
  useEffect(() => {
    if (!target || completedRef.current) return
    if (wordStates.forKey !== target) return
    if (targetWords.length === 0 || wordStates.states.length !== targetWords.length) return

    // Only ever complete on a line you actually said. No timer, no partial
    // credit — those advanced the line while you were still speaking. If a word
    // stubbornly won't match, the ✓ button moves on deliberately.
    const hits = wordStates.states.filter(s => s === 'hit').length
    if (hits === 0 || hits !== wordStates.states.length) return

    completedRef.current = true
    const el = lineRefs.current[idx]
    if (el) {
      const rect = el.getBoundingClientRect()
      celebrate(rect.left + rect.width / 2, rect.top + rect.height / 2, { count: 30, spread: 170 })
      gsap.fromTo(el, { scale: 1 }, { scale: 1.06, duration: 0.18, yoyo: true, repeat: 1, ease: 'power2.out', clearProps: 'scale' })
    }
    const said = target
    sendTimerRef.current = setTimeout(() => sendUserText(said), 900)
  }, [wordStates, targetWords.length, target, idx, sendUserText])

  // Gloss the newest partner line for the translation toggle (target-only modes;
  // tutor turns are already mostly in your own language).
  useEffect(() => {
    if (!showTranslation || mode === 'tutor' || !isVisible) return
    const i = messages.length - 1
    const m = messages[i]
    if (!m || m.role !== 'assistant' || m.translation) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await macStudioFetch('generate', JSON.stringify({
            model: selectedModel,
            prompt: `Translate this ${targetLang.label} sentence into natural ${nativeLang.label}. Respond with ONLY the translation.\n\n"${m.content}"`,
            stream: false, keep_alive: '30m',
            options: { temperature: 0.1, num_predict: 60 }
          }))
        if (!res.ok) return
        const data = await res.json()
        const t = (data.response || '').trim()
        if (!cancelled && t) setMessages(prev => prev.map((mm, j) => (j === i ? { ...mm, translation: t } : mm)))
      } catch { /* optional */ }
    })()
    return () => { cancelled = true }
  }, [showTranslation, messages, mode, isVisible, selectedModel, targetLang, nativeLang])

  useEffect(() => {
    if (!listeningRef.current) return
    sessionRef.current++
    try { recRef.current?.abort() } catch { /* fine */ }
    const t = setTimeout(() => { if (listeningRef.current) startListening() }, 350)
    return () => clearTimeout(t)
  }, [target]) // eslint-disable-line

  // Only ever start (and speak) once the view is actually on screen — this
  // component stays mounted behind the HUD, so an unguarded start would greet
  // you out loud the moment you log in.
  useEffect(() => {
    if (!isVisible) {
      stopSpeaking()
      stopListening()
      return
    }
    if (startedRef.current) return
    startedRef.current = true
    startConversation(nativeLang, targetLang, selectedScenario, customScenarioText)
    // eslint-disable-next-line
  }, [isVisible])

  // Language / mode / scenario changes restart the session — but only while visible.
  useEffect(() => {
    if (!isVisible || !startedRef.current) return
    startConversation(nativeLang, targetLang, selectedScenario, customScenarioText)
    // eslint-disable-next-line
  }, [nativeLang.voice, targetLang.voice, selectedScenario.id, mode])

  useEffect(() => () => {
    clearTimeout(sendTimerRef.current)
    clearTimeout(debounceRef.current)
    listeningRef.current = false
    sessionRef.current++
    ttsTokenRef.current++
    try { recRef.current?.abort() } catch { /* fine */ }
    cancelTts()
  }, [])

  // ── Actions ────────────────────────────────────────────────────────────────

  const practise = (phrase) => {
    if (!phrase) return
    targetRef.current = phrase
    targetWordsRef.current = tokenizeLine(phrase, targetLang.voice)
    heardRef.current = ''
    sawFinalRef.current = false
    completedRef.current = false
    setTarget(phrase)
    setWordStates({ forKey: phrase, states: Array(targetWordsRef.current.length).fill('pending') })
    setInterim('')
    setMicError(null)
    // Hard-reset the recognizer: reusing a live session would carry its
    // accumulated transcript into this line and finish it without you speaking.
    sessionRef.current++
    try { recRef.current?.abort() } catch { /* nothing running */ }
    listeningRef.current = false
    setListening(false)
    setMicPhase('off')
    setTimeout(() => startListeningRef.current?.(), 300)
  }

  const chooseSuggestion = (text) => {
    if (showKeyboard && input.trim()) {
      const newVal = `${input.trim()} ${text}`
      setInput(newVal)
      inputRef.current?.focus()
      clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => fetchCompletions(newVal.trim(), messagesRef.current), 400)
      return
    }
    if (mode === 'free') { sendUserText(text); return }
    practise(text)
  }

  const retryLine = () => {
    clearTimeout(sendTimerRef.current)
    completedRef.current = false
    heardRef.current = ''
    sawFinalRef.current = false
    setWordStates({ forKey: target || '', states: Array(targetWords.length).fill('pending') })
    setInterim('')
    if (listeningRef.current) {
      sessionRef.current++
      try { recRef.current?.abort() } catch { /* fine */ }
      setTimeout(() => startListeningRef.current?.(), 250)
    }
  }

  const handleMicTap = () => {
    if (!listening) { heardRef.current = ''; sawFinalRef.current = false }
    if (listening) {
      stopListening()
      if (!target && freeTranscriptRef.current.trim()) sendUserText(freeTranscriptRef.current)
      return
    }
    startListening()
  }

  const handleWordTap = (i) => {
    if (!targetWords[i]) return
    speakOne(targetWords[i].raw, targetLang.voice)
    setWordStates(prev => (prev.forKey !== target ? prev : {
      forKey: prev.forKey,
      states: prev.states.map((s, j) => (j === i && s === 'miss' ? 'pending' : s)),
    }))
  }

  const handleTyping = (e) => {
    const val = e.target.value
    setInput(val)
    clearTimeout(debounceRef.current)
    if (val.trim().length < 3) return
    debounceRef.current = setTimeout(() => fetchCompletions(val.trim(), messagesRef.current), 400)
  }

  const replayLine = () => {
    const m = [...messages].reverse().find(x => x.role === 'assistant')
    if (!m) return
    speakSegments(m.segments ? segsToVoiced(m.segments) : [{ text: m.content, voiceURI: voices[targetLang.voice] }])
  }

  const roman = romanizationOf(line?.pending ? line.content : null, targetLang.voice)
  const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant')

  const toggleTheme = () => setTheme(t => {
    const next = t === 'dark' ? 'light' : 'dark'
    saveTheme(next)
    return next
  })

  const handleClose = () => {
    stopSpeaking()
    stopListening()
    setIsVisible?.(false)
  }

  return (
    <div className={`${styles.view} ${styles[theme]}`}>
      <button className={styles.themeBtn} onClick={toggleTheme} title={theme === 'dark' ? 'Light mode' : 'Dark mode'}>
        {theme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
      </button>
      <button className={styles.closeBtn} onClick={handleClose} title="Close"><CloseIcon /></button>

      {/* Top bar */}
      <div className={styles.topBar}>
        <span className={styles.modeLabel}>Partner</span>

        <div className={styles.langPair}>
          <select
            className={styles.langSelect}
            value={nativeLang.voice}
            onChange={e => setNativeLang(langByCode(e.target.value, LANGUAGES[0]))}
            title="The language you already speak — the tutor explains in this"
          >
            {LANGUAGES.map(l => <option key={l.voice} value={l.voice}>{l.flag} I speak {l.label}</option>)}
          </select>
          <span className={styles.langArrow}>→</span>
          <select
            className={`${styles.langSelect} ${styles.langSelectTarget}`}
            value={targetLang.voice}
            onChange={e => setTargetLang(langByCode(e.target.value, LANGUAGES[1]))}
            title="The language you're practising"
          >
            {LANGUAGES.map(l => <option key={l.voice} value={l.voice}>{l.flag} learning {l.label}</option>)}
          </select>
        </div>

        <div className={styles.modeGroup}>
          {MODES.map(m => (
            <button
              key={m.id}
              className={`${styles.modeSwitch} ${mode === m.id ? styles.modeSwitchActive : ''}`}
              onClick={() => { setMode(m.id); setTarget(null); stopListening(); stopSpeaking() }}
              title={m.hint}
            >{m.label}</button>
          ))}
        </div>

        <select
          className={`${styles.langSelect} ${styles.modelSelect}`}
          value={selectedModel}
          onChange={e => setSelectedModel(e.target.value)}
          title="Which Ollama model answers. Bigger = more accurate target language, slower. Takes effect on the next turn."
        >
          {modelOptions.map(m => <option key={m} value={m}>🧠 {m}</option>)}
        </select>

        <label className={styles.toggle}>
          <input type="checkbox" checked={autoSpeak} onChange={e => { setAutoSpeak(e.target.checked); if (!e.target.checked) stopSpeaking() }} />
          auto-speak
        </label>
        <label className={styles.toggle}>
          <input type="checkbox" checked={showRoman} onChange={e => setShowRoman(e.target.checked)} />
          romanization
        </label>
        {mode !== 'tutor' && (
          <label className={styles.toggle}>
            <input type="checkbox" checked={showTranslation} onChange={e => setShowTranslation(e.target.checked)} />
            translation
          </label>
        )}
        <button className={styles.settingsBtn} onClick={() => setSettingsOpen(o => !o)}>
          {selectedScenario.icon} {selectedScenario.label} ▾
        </button>
      </div>

      {settingsOpen && (
        <div className={styles.settingsPanel}>
          <div className={styles.settingsRow}>
            <span className={styles.settingsLabel}>scenario</span>
            <div className={styles.settingsChips}>
              {SCENARIOS.map(sc => (
                <button
                  key={sc.id}
                  className={`${styles.settingsChip} ${sc.id === selectedScenario.id ? styles.settingsChipActive : ''}`}
                  onClick={() => { setSelectedScenario(sc); if (sc.id !== 'custom') setSettingsOpen(false) }}
                >{sc.icon} {sc.label}</button>
              ))}
            </div>
          </div>
          {selectedScenario.id === 'custom' && (
            <div className={styles.settingsRow}>
              <span className={styles.settingsLabel}>describe</span>
              <input
                className={styles.settingsInput}
                value={customScenarioText}
                onChange={e => setCustomScenarioText(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && customScenarioText.trim()) {
                    startConversation(nativeLang, targetLang, selectedScenario, customScenarioText.trim())
                    setSettingsOpen(false)
                  }
                }}
                placeholder="e.g. returning a faulty item…"
              />
            </div>
          )}
          <div className={styles.settingsRow}>
            <span className={styles.settingsLabel}>session</span>
            <button
              className={styles.settingsChip}
              onClick={() => { startConversation(nativeLang, targetLang, selectedScenario, customScenarioText); setSettingsOpen(false) }}
            ><RestartAltIcon fontSize="small" /> restart conversation</button>
          </div>
        </div>
      )}

      {/* Conversation */}
      <div className={styles.viewport} ref={viewportRef}>
        <div className={styles.fadeTop} />
        <div className={styles.fadeBottom} />
        <div className={styles.track} ref={trackRef}>
          {lines.map((l, i) => {
            const isCurrent = i === idx
            const cls = [
              styles.line,
              l.role === 'assistant' ? styles.linePartner : styles.lineMine,
              isCurrent ? styles.lineCurrent : styles.linePast,
            ].join(' ')
            return (
              <div key={l.key} ref={el => { lineRefs.current[i] = el }} className={cls}>
                {isCurrent && l.pending ? (
                  <>
                    <div className={styles.lineWords}>
                      {targetWords.map((w, wi) => (
                        <span
                          key={wi}
                          className={[
                            styles.word,
                            states[wi] === 'hit' ? styles.wordHit : '',
                            states[wi] === 'miss' ? styles.wordMiss : '',
                          ].join(' ')}
                          onClick={(e) => { e.stopPropagation(); handleWordTap(wi) }}
                        >{w.raw}</span>
                      ))}
                    </div>
                    {showRoman && roman && <div className={styles.lineRoman}>{roman}</div>}
                    <div className={styles.lineHint}>say this in {targetLang.label}</div>
                  </>
                ) : l.segments ? (
                  <>
                    <div>
                      {l.segments.map((s, si) => (
                        <span
                          key={si}
                          className={s.lang === 'target' ? styles.segTarget : styles.segNative}
                          onClick={() => speakOne(s.text, s.lang === 'target' ? targetLang.voice : nativeLang.voice)}
                          title={s.lang === 'target' ? `Hear it in ${targetLang.label}` : 'Hear it'}
                        >{s.text}{' '}</span>
                      ))}
                    </div>
                    {isCurrent && l.tip && <div className={styles.lineTip}>💡 {l.tip}</div>}
                  </>
                ) : (
                  <>
                    <div onClick={() => speakOne(l.content, l.role === 'assistant' ? targetLang.voice : targetLang.voice)}>{l.content}</div>
                    {isCurrent && showTranslation && l.translation && (
                      <div className={styles.lineTranslation}>{l.translation}</div>
                    )}
                  </>
                )}
              </div>
            )
          })}
          {loadingChat && (
            <div className={`${styles.line} ${styles.linePartner} ${styles.lineCurrent}`}>
              <div className={styles.typing}><span /><span /><span /></div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom bar */}
      <div className={styles.bottomBar}>
        <div className={styles.interim}>
          {micError
            ? micError
            : listening
              ? (interim || (
                  micPhase === 'speech' ? `hearing you… (${recLangLabel})`
                    : micPhase === 'sound' ? `picking up sound… (${recLangLabel})`
                    : `mic open (${recLangLabel}) — no sound yet`
                ))
              : target ? `read it aloud in ${targetLang.label}`
              : mode === 'tutor' ? 'tap a phrase to practise, or just answer'
              : ''}
        </div>

        {/* Tutor: the phrase to try */}
        {mode === 'tutor' && !target && lastAssistant?.try && (
          <button className={styles.tryChip} onClick={() => practise(lastAssistant.try)}>
            <RecordVoiceOverIcon style={{ fontSize: 17 }} />
            <span>try saying: <strong>{lastAssistant.try}</strong></span>
            <span
              className={styles.suggestionSpeak}
              onClick={(e) => { e.stopPropagation(); speakOne(lastAssistant.try, targetLang.voice) }}
              title="Hear it"
            ><VolumeUpIcon style={{ fontSize: 15 }} /></span>
          </button>
        )}

        {/* Guided/free suggestions */}
        {mode !== 'tutor' && (
          <div className={styles.suggestions}>
            {loadingSuggestions && <div className={styles.typing}><span /><span /><span /></div>}
            {!loadingSuggestions && suggestions.map((s, i) => (
              <button key={i} className={styles.suggestion} onClick={() => chooseSuggestion(s)}>
                <span className={styles.suggestionNum}>{i + 1}</span>
                <span>{s}</span>
                <span
                  className={styles.suggestionSpeak}
                  onClick={(e) => { e.stopPropagation(); speakOne(s, targetLang.voice) }}
                  title="Hear it"
                ><VolumeUpIcon style={{ fontSize: 15 }} /></span>
              </button>
            ))}
          </div>
        )}

        {/* Which language the recognizer listens for */}
        {!target && (
          <div className={styles.answerLangRow}>
            <span className={styles.answerLangLabel}>listen for</span>
            <button
              className={`${styles.answerLangBtn} ${answerLang === 'native' ? styles.answerLangActive : ''}`}
              onClick={() => setAnswerLang('native')}
            >{nativeLang.flag} {nativeLang.label}</button>
            <button
              className={`${styles.answerLangBtn} ${answerLang === 'target' ? styles.answerLangActive : ''}`}
              onClick={() => setAnswerLang('target')}
            >{targetLang.flag} {targetLang.label}</button>
          </div>
        )}

        {showKeyboard && (
          <div className={styles.keyboardRow}>
            <textarea
              ref={inputRef}
              className={styles.textarea}
              rows={1}
              value={input}
              onChange={handleTyping}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendUserText(input) }
              }}
              placeholder={`Type in ${targetLang.label} or ${nativeLang.label}…`}
              spellCheck="false"
            />
            <button className={styles.sideBtn} onClick={() => sendUserText(input)} disabled={!input.trim() || loadingChat} title="Send">
              <SendIcon />
            </button>
          </div>
        )}

        <div className={styles.controls}>
          <button
            className={`${styles.sideBtn} ${showKeyboard ? styles.sideBtnActive : ''}`}
            onClick={() => setShowKeyboard(k => !k)}
            title="Type instead"
          ><KeyboardIcon /></button>
          <button className={styles.sideBtn} onClick={replayLine} title="Hear that again"><VolumeUpIcon /></button>
          <button
            className={`${styles.micBtn} ${listening ? styles.micBtnActive : ''}`}
            onClick={handleMicTap}
            title={listening ? 'Stop the mic' : 'Speak'}
          >
            {listening ? <MicIcon style={{ fontSize: 34 }} /> : <MicOffIcon style={{ fontSize: 34 }} />}
          </button>
          <button className={styles.sideBtn} onClick={retryLine} disabled={!target} title="Retry this line">
            <ReplayIcon />
          </button>
          <button
            className={styles.sideBtn}
            onClick={() => {
              if (!target) return
              clearTimeout(sendTimerRef.current)
              completedRef.current = true
              sendUserText(target)
            }}
            disabled={!target}
            title="Close enough — accept and move on"
          ><CheckIcon /></button>
          <button
            className={styles.sideBtn}
            onClick={() => startConversation(nativeLang, targetLang, selectedScenario, customScenarioText)}
            title="Restart the conversation"
          ><RestartAltIcon /></button>
        </div>
        <div className={styles.progress}>{messages.filter(m => m.role === 'user').length} turns</div>
      </div>
    </div>
  )
}
